export type Json =
  | string
  | number
  | boolean
  | null
  | { [key: string]: Json | undefined }
  | Json[]

// Enums
// Note: Database enum is still 'production_status' for migration safety
// We alias it to ProductStatus for cleaner TypeScript usage
export type ProductStatus = 'draft' | 'scheduled' | 'featured' | 'archived' | 'hidden'
export type FulfillmentType = 'direct' | 'manufacturer'
export type FulfillmentStatus = 'pending' | 'processing' | 'shipped' | 'delivered' | 'cancelled'
export type AdminRole = 'owner' | 'admin' | 'editor'
export type ProcurementStatus = 'pending' | 'ordered_from_vendor' | 'vendor_shipped' | 'received_tracking' | 'issue'

// Storage bucket constant - kept as 'production-images' to avoid URL breakage
export const PRODUCT_MEDIA_BUCKET = 'production-images'

export interface Database {
  public: {
    Tables: {
      products: {
        Row: {
          id: string
          title: string
          slug: string
          description: string | null
          short_description: string | null
          video_url: string | null
          video_thumbnail_url: string | null
          original_price: number
          featured_discount_percent: number
          archive_discount_percent: number
          affiliate_link: string | null
          status: ProductStatus
          featured_start_date: string | null
          featured_end_date: string | null
          view_count: number
          click_count: number
          created_at: string
          updated_at: string
          manufacturer_name: string | null
          manufacturer_email: string | null
          manufacturer_order_url: string | null
          cost_price: number | null
          estimated_ship_days: number
          product_images: Json
          hero_image_url: string | null
          key_features: Json
          specs: Json
          drop_theme: string | null
          week_number: number | null
          year: number | null
          is_weekly_drop: boolean
          media: Json
          sku: string | null
          options: Json
          variants: Json
          custom_inputs: Json
        }
        Insert: {
          id?: string
          title: string
          slug: string
          description?: string | null
          short_description?: string | null
          video_url?: string | null
          video_thumbnail_url?: string | null
          original_price: number
          featured_discount_percent?: number
          archive_discount_percent?: number
          affiliate_link?: string | null
          status?: ProductStatus
          featured_start_date?: string | null
          featured_end_date?: string | null
          view_count?: number
          click_count?: number
          created_at?: string
          updated_at?: string
          manufacturer_name?: string | null
          manufacturer_email?: string | null
          manufacturer_order_url?: string | null
          cost_price?: number | null
          estimated_ship_days?: number
          product_images?: Json
          hero_image_url?: string | null
          key_features?: Json
          specs?: Json
          drop_theme?: string | null
          week_number?: number | null
          year?: number | null
          is_weekly_drop?: boolean
          media?: Json
          sku?: string | null
          options?: Json
          variants?: Json
          custom_inputs?: Json
        }
        Update: {
          id?: string
          title?: string
          slug?: string
          description?: string | null
          short_description?: string | null
          video_url?: string | null
          video_thumbnail_url?: string | null
          original_price?: number
          featured_discount_percent?: number
          archive_discount_percent?: number
          affiliate_link?: string | null
          status?: ProductStatus
          featured_start_date?: string | null
          featured_end_date?: string | null
          view_count?: number
          click_count?: number
          created_at?: string
          updated_at?: string
          manufacturer_name?: string | null
          manufacturer_email?: string | null
          manufacturer_order_url?: string | null
          cost_price?: number | null
          estimated_ship_days?: number
          product_images?: Json
          hero_image_url?: string | null
          key_features?: Json
          specs?: Json
          drop_theme?: string | null
          week_number?: number | null
          year?: number | null
          is_weekly_drop?: boolean
          media?: Json
          sku?: string | null
          options?: Json
          variants?: Json
          custom_inputs?: Json
        }
        Relationships: []
      }
      orders: {
        Row: {
          id: string
          product_id: string | null
          user_id: string | null
          stripe_session_id: string | null
          stripe_payment_intent_id: string | null
          customer_email: string
          customer_name: string | null
          customer_phone: string | null
          shipping_address: Json | null
          quantity: number
          unit_price: number | null
          discount_percent: number | null
          total_amount: number
          fulfillment_type: FulfillmentType | null
          fulfillment_status: FulfillmentStatus
          manufacturer_order_id: string | null
          tracking_number: string | null
          tracking_url: string | null
          shipped_at: string | null
          delivered_at: string | null
          admin_notes: string | null
          customer_notes: string | null
          created_at: string
          updated_at: string
          item_count: number
          subtotal: number | null
          total_savings: number | null
          is_archived: boolean
        }
        Insert: {
          id?: string
          product_id?: string | null
          user_id?: string | null
          stripe_session_id?: string | null
          stripe_payment_intent_id?: string | null
          customer_email: string
          customer_name?: string | null
          customer_phone?: string | null
          shipping_address?: Json | null
          quantity?: number
          unit_price?: number | null
          discount_percent?: number | null
          total_amount: number
          fulfillment_type?: FulfillmentType | null
          fulfillment_status?: FulfillmentStatus
          manufacturer_order_id?: string | null
          tracking_number?: string | null
          tracking_url?: string | null
          shipped_at?: string | null
          delivered_at?: string | null
          admin_notes?: string | null
          customer_notes?: string | null
          created_at?: string
          updated_at?: string
          item_count?: number
          subtotal?: number | null
          total_savings?: number | null
          is_archived?: boolean
        }
        Update: {
          id?: string
          product_id?: string | null
          user_id?: string | null
          stripe_session_id?: string | null
          stripe_payment_intent_id?: string | null
          customer_email?: string
          customer_name?: string | null
          customer_phone?: string | null
          shipping_address?: Json | null
          quantity?: number
          unit_price?: number | null
          discount_percent?: number | null
          total_amount?: number
          fulfillment_type?: FulfillmentType | null
          fulfillment_status?: FulfillmentStatus
          manufacturer_order_id?: string | null
          tracking_number?: string | null
          tracking_url?: string | null
          shipped_at?: string | null
          delivered_at?: string | null
          admin_notes?: string | null
          customer_notes?: string | null
          created_at?: string
          updated_at?: string
          item_count?: number
          subtotal?: number | null
          total_savings?: number | null
          is_archived?: boolean
        }
        Relationships: [
          {
            foreignKeyName: 'orders_product_id_fkey'
            columns: ['product_id']
            isOneToOne: false
            referencedRelation: 'products'
            referencedColumns: ['id']
          }
        ]
      }
      order_items: {
        Row: {
          id: string
          order_id: string
          product_id: string | null
          quantity: number
          unit_price: number
          discount_percent: number
          line_total: number
          product_title: string | null
          product_thumbnail_url: string | null
          manufacturer_order_id: string | null
          procurement_status: ProcurementStatus
          procurement_notes: string | null
          vendor_tracking_number: string | null
          vendor_shipped_at: string | null
          created_at: string
          variant_id: string | null
          variant_label: string | null
          variant_sku: string | null
          custom_inputs: Json | null
        }
        Insert: {
          id?: string
          order_id: string
          product_id?: string | null
          quantity?: number
          unit_price: number
          discount_percent?: number
          line_total: number
          product_title?: string | null
          product_thumbnail_url?: string | null
          manufacturer_order_id?: string | null
          procurement_status?: ProcurementStatus
          procurement_notes?: string | null
          vendor_tracking_number?: string | null
          vendor_shipped_at?: string | null
          created_at?: string
          variant_id?: string | null
          variant_label?: string | null
          variant_sku?: string | null
          custom_inputs?: Json | null
        }
        Update: {
          id?: string
          order_id?: string
          product_id?: string | null
          quantity?: number
          unit_price?: number
          discount_percent?: number
          line_total?: number
          product_title?: string | null
          product_thumbnail_url?: string | null
          manufacturer_order_id?: string | null
          procurement_status?: ProcurementStatus
          procurement_notes?: string | null
          vendor_tracking_number?: string | null
          vendor_shipped_at?: string | null
          created_at?: string
          variant_id?: string | null
          variant_label?: string | null
          variant_sku?: string | null
          custom_inputs?: Json | null
        }
        Relationships: [
          {
            foreignKeyName: 'order_items_order_id_fkey'
            columns: ['order_id']
            isOneToOne: false
            referencedRelation: 'orders'
            referencedColumns: ['id']
          },
          {
            foreignKeyName: 'order_items_product_id_fkey'
            columns: ['product_id']
            isOneToOne: false
            referencedRelation: 'products'
            referencedColumns: ['id']
          }
        ]
      }
      carts: {
        Row: {
          id: string
          user_id: string
          created_at: string
          updated_at: string
        }
        Insert: {
          id?: string
          user_id: string
          created_at?: string
          updated_at?: string
        }
        Update: {
          id?: string
          user_id?: string
          created_at?: string
          updated_at?: string
        }
        Relationships: []
      }
      cart_items: {
        Row: {
          id: string
          cart_id: string
          product_id: string
          quantity: number
          added_at: string
        }
        Insert: {
          id?: string
          cart_id: string
          product_id: string
          quantity?: number
          added_at?: string
        }
        Update: {
          id?: string
          cart_id?: string
          product_id?: string
          quantity?: number
          added_at?: string
        }
        Relationships: [
          {
            foreignKeyName: 'cart_items_cart_id_fkey'
            columns: ['cart_id']
            isOneToOne: false
            referencedRelation: 'carts'
            referencedColumns: ['id']
          },
          {
            foreignKeyName: 'cart_items_product_id_fkey'
            columns: ['product_id']
            isOneToOne: false
            referencedRelation: 'products'
            referencedColumns: ['id']
          }
        ]
      }
      analytics_events: {
        Row: {
          id: string
          product_id: string | null
          event_type: string
          session_id: string | null
          user_agent: string | null
          ip_address: string | null
          referrer: string | null
          created_at: string
        }
        Insert: {
          id?: string
          product_id?: string | null
          event_type: string
          session_id?: string | null
          user_agent?: string | null
          ip_address?: string | null
          referrer?: string | null
          created_at?: string
        }
        Update: {
          id?: string
          product_id?: string | null
          event_type?: string
          session_id?: string | null
          user_agent?: string | null
          ip_address?: string | null
          referrer?: string | null
          created_at?: string
        }
        Relationships: []
      }
      site_settings: {
        Row: {
          id: string
          key: string
          value: Json
          updated_at: string
        }
        Insert: {
          id?: string
          key: string
          value: Json
          updated_at?: string
        }
        Update: {
          id?: string
          key?: string
          value?: Json
          updated_at?: string
        }
        Relationships: []
      }
      admin_users: {
        Row: {
          id: string
          email: string
          name: string | null
          role: AdminRole
          invited_by: string | null
          invited_at: string | null
          last_login_at: string | null
          created_at: string
          updated_at: string
        }
        Insert: {
          id: string
          email: string
          name?: string | null
          role?: AdminRole
          invited_by?: string | null
          invited_at?: string | null
          last_login_at?: string | null
          created_at?: string
          updated_at?: string
        }
        Update: {
          id?: string
          email?: string
          name?: string | null
          role?: AdminRole
          invited_by?: string | null
          invited_at?: string | null
          last_login_at?: string | null
          created_at?: string
          updated_at?: string
        }
        Relationships: []
      }
      customer_profiles: {
        Row: {
          id: string
          email: string
          display_name: string | null
          phone: string | null
          default_shipping_address: Json | null
          stripe_customer_id: string | null
          email_notifications: boolean
          created_at: string
          updated_at: string
        }
        Insert: {
          id: string
          email: string
          display_name?: string | null
          phone?: string | null
          default_shipping_address?: Json | null
          stripe_customer_id?: string | null
          email_notifications?: boolean
          created_at?: string
          updated_at?: string
        }
        Update: {
          id?: string
          email?: string
          display_name?: string | null
          phone?: string | null
          default_shipping_address?: Json | null
          stripe_customer_id?: string | null
          email_notifications?: boolean
          created_at?: string
          updated_at?: string
        }
        Relationships: []
      }
      admin_invitations: {
        Row: {
          id: string
          email: string
          role: AdminRole
          invited_by: string
          token: string
          expires_at: string
          accepted_at: string | null
          created_at: string
        }
        Insert: {
          id?: string
          email: string
          role?: AdminRole
          invited_by: string
          token: string
          expires_at?: string
          accepted_at?: string | null
          created_at?: string
        }
        Update: {
          id?: string
          email?: string
          role?: AdminRole
          invited_by?: string
          token?: string
          expires_at?: string
          accepted_at?: string | null
          created_at?: string
        }
        Relationships: []
      }
    }
    Views: {
      current_featured_product: {
        Row: {
          id: string
          title: string
          slug: string
          description: string | null
          short_description: string | null
          video_url: string | null
          video_thumbnail_url: string | null
          original_price: number
          featured_discount_percent: number
          archive_discount_percent: number
          affiliate_link: string | null
          status: ProductStatus
          featured_start_date: string | null
          featured_end_date: string | null
          view_count: number
          click_count: number
          created_at: string
          updated_at: string
          manufacturer_name: string | null
          manufacturer_email: string | null
          manufacturer_order_url: string | null
          cost_price: number | null
          estimated_ship_days: number
          product_images: Json
          hero_image_url: string | null
          key_features: Json
          specs: Json
          drop_theme: string | null
          week_number: number | null
          year: number | null
          is_weekly_drop: boolean
          media: Json
          sku: string | null
          options: Json
          variants: Json
          custom_inputs: Json
        }
        Relationships: []
      }
      archive_products: {
        Row: {
          id: string
          title: string
          slug: string
          description: string | null
          short_description: string | null
          video_url: string | null
          video_thumbnail_url: string | null
          original_price: number
          featured_discount_percent: number
          archive_discount_percent: number
          affiliate_link: string | null
          status: ProductStatus
          featured_start_date: string | null
          featured_end_date: string | null
          view_count: number
          click_count: number
          created_at: string
          updated_at: string
          manufacturer_name: string | null
          manufacturer_email: string | null
          manufacturer_order_url: string | null
          cost_price: number | null
          estimated_ship_days: number
          product_images: Json
          hero_image_url: string | null
          key_features: Json
          specs: Json
          drop_theme: string | null
          week_number: number | null
          year: number | null
          is_weekly_drop: boolean
          media: Json
          sku: string | null
          options: Json
          variants: Json
          custom_inputs: Json
        }
        Relationships: []
      }
      scheduled_products_queue: {
        Row: {
          id: string
          title: string
          slug: string
          description: string | null
          short_description: string | null
          video_url: string | null
          video_thumbnail_url: string | null
          original_price: number
          featured_discount_percent: number
          archive_discount_percent: number
          affiliate_link: string | null
          status: ProductStatus
          featured_start_date: string | null
          featured_end_date: string | null
          view_count: number
          click_count: number
          created_at: string
          updated_at: string
          manufacturer_name: string | null
          manufacturer_email: string | null
          manufacturer_order_url: string | null
          cost_price: number | null
          estimated_ship_days: number
          product_images: Json
          hero_image_url: string | null
          key_features: Json
          specs: Json
          drop_theme: string | null
          week_number: number | null
          year: number | null
          is_weekly_drop: boolean
          media: Json
          sku: string | null
          options: Json
          variants: Json
          custom_inputs: Json
        }
        Relationships: []
      }
    }
    Functions: {
      is_admin: {
        Args: Record<string, never>
        Returns: boolean
      }
      get_admin_role: {
        Args: Record<string, never>
        Returns: AdminRole | null
      }
      can_manage_role: {
        Args: { target_role: AdminRole }
        Returns: boolean
      }
      has_role_level: {
        Args: { required_role: AdminRole }
        Returns: boolean
      }
      order_has_items: {
        Args: { order_uuid: string }
        Returns: boolean
      }
    }
    Enums: {
      production_status: ProductStatus
      fulfillment_type: FulfillmentType
      fulfillment_status: FulfillmentStatus
      admin_role: AdminRole
      procurement_status: ProcurementStatus
    }
  }
}

// Convenience types - Primary types (new naming)
export type Product = Database['public']['Tables']['products']['Row']
export type ProductInsert = Database['public']['Tables']['products']['Insert']
export type ProductUpdate = Database['public']['Tables']['products']['Update']

// Deprecated aliases for backward compatibility during migration
/** @deprecated Use Product instead */
export type Production = Product
/** @deprecated Use ProductInsert instead */
export type ProductionInsert = ProductInsert
/** @deprecated Use ProductUpdate instead */
export type ProductionUpdate = ProductUpdate
/** @deprecated Use ProductStatus instead */
export type ProductionStatus = ProductStatus

export type Order = Database['public']['Tables']['orders']['Row']
export type OrderInsert = Database['public']['Tables']['orders']['Insert']
export type OrderUpdate = Database['public']['Tables']['orders']['Update']

export type OrderItem = Database['public']['Tables']['order_items']['Row']
export type OrderItemInsert = Database['public']['Tables']['order_items']['Insert']
export type OrderItemUpdate = Database['public']['Tables']['order_items']['Update']

export type Cart = Database['public']['Tables']['carts']['Row']
export type CartInsert = Database['public']['Tables']['carts']['Insert']
export type CartUpdate = Database['public']['Tables']['carts']['Update']

export type CartItem = Database['public']['Tables']['cart_items']['Row']
export type CartItemInsert = Database['public']['Tables']['cart_items']['Insert']
export type CartItemUpdate = Database['public']['Tables']['cart_items']['Update']

export type AnalyticsEvent = Database['public']['Tables']['analytics_events']['Row']
export type AnalyticsEventInsert = Database['public']['Tables']['analytics_events']['Insert']

export type SiteSetting = Database['public']['Tables']['site_settings']['Row']

export type AdminUser = Database['public']['Tables']['admin_users']['Row']
export type AdminUserInsert = Database['public']['Tables']['admin_users']['Insert']
export type AdminUserUpdate = Database['public']['Tables']['admin_users']['Update']

export type CustomerProfile = Database['public']['Tables']['customer_profiles']['Row']
export type CustomerProfileInsert = Database['public']['Tables']['customer_profiles']['Insert']
export type CustomerProfileUpdate = Database['public']['Tables']['customer_profiles']['Update']

export type AdminInvitation = Database['public']['Tables']['admin_invitations']['Row']
export type AdminInvitationInsert = Database['public']['Tables']['admin_invitations']['Insert']
export type AdminInvitationUpdate = Database['public']['Tables']['admin_invitations']['Update']

// Shipping address type
export interface ShippingAddress {
  [key: string]: string | undefined
  name: string
  line1: string
  line2?: string
  city: string
  state: string
  postal_code: string
  country: string
}

// Order with product details (for display)
export interface OrderWithProduct extends Order {
  product: Product | null
}

// Deprecated alias
/** @deprecated Use OrderWithProduct instead */
export type OrderWithProduction = OrderWithProduct

// Order with items (for multi-product orders)
export interface OrderWithItems extends Order {
  items: OrderItem[]
  product?: Product | null // Legacy support
}

// Cart with items and product details
export interface CartItemWithProduct extends CartItem {
  product: Product
}

// Deprecated alias
/** @deprecated Use CartItemWithProduct instead */
export type CartItemWithProduction = CartItemWithProduct

export interface CartWithItems extends Cart {
  items: CartItemWithProduct[]
}

// Typed versions of JSONB fields
export type ProductImages = string[]
export type KeyFeatures = string[]
export type ProductSpecs = Record<string, string>

// Unified media item type for the media JSONB array
export interface MediaItem {
  type: 'image' | 'video'
  url: string
  thumbnail_url?: string // Optional, primarily used for video thumbnails
  is_review?: boolean // Designates the review video (only one video can be marked as review)
}

// Helper type for the media array
export type ProductMedia = MediaItem[]

// Product option axis definition (e.g., Size, Color)
export interface ProductOption {
  id: string
  name: string
  values: string[]
  position: number
}

// Variant combination (auto-generated from options, admin-editable)
export interface ProductVariant {
  id: string
  option_values: Record<string, string>  // {"Size": "L", "Color": "Red"}
  sku: string | null
  price_adjustment: number  // +/- from original_price (default 0)
  image_url: string | null  // variant-specific image
  available: boolean
  position: number
  sku_locked?: boolean  // Client-side only: true if admin manually set the SKU
}

// Customer input field definition
export interface CustomInputDefinition {
  id: string
  label: string
  type: 'text' | 'textarea' | 'image_upload' | 'file_upload'
  required: boolean
  placeholder?: string
  max_length?: number
  help_text?: string
  position: number
}
